/**
 * LastSyncChip — Dashboard header sync-state indicator.
 *
 * Renders one of 7 states (never_synced, syncing, synced_recent, synced_stale,
 * partial_failure, total_failure, rate_limited) derived by selectLastSyncSummary.
 *
 * Placement: left of the Sync button in the dashboard canvas top bar (DQ1 answer).
 *
 * Mockup: designs/sync-error-handling/2026-05-05-last-sync-chip-v1.html
 * Brand guide: §34.7
 * Plan ref: §3 D3. TDD ref: §6.4.
 */

import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { CheckIcon, ClockIcon, AlertTriangleIcon, XIcon, Loader } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { selectLastSyncSummary } from '@/redux/slice/dashboardViewSlice'
import type {
  LastSyncPerDomainEntry,
  LastSyncState,
} from '@/types/LastSync.types'

// ─── Relative-time helper ─────────────────────────────────────────────────────

/**
 * Returns a human-readable relative time string like "4 minutes ago",
 * "just now", "1 hour ago", etc.
 * Does NOT use date-fns to keep the impl simple and avoid any edge cases
 * with the library's "less than a minute" granularity.
 */
function formatRelativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime()
  const diffSec = Math.max(0, Math.floor(diffMs / 1000))

  if (diffSec < 30) return 'just now'
  if (diffSec < 90) return '30 seconds ago'

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`

  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`

  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
}

/**
 * Returns a short compact label for narrow header contexts:
 * "4m ago" / "2h ago" / "Failed" / "Paused"
 */
function formatCompactTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime()
  const diffSec = Math.max(0, Math.floor(diffMs / 1000))

  if (diffSec < 60) return `${diffSec}s ago`

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`

  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`

  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

/**
 * Formats an ISO string as a locale time string, e.g. "2:34 PM, May 5 2026".
 * Used in ARIA labels and popover header for full-precision timestamp.
 */
function formatAbsoluteTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ─── Error category shorthand for popover ────────────────────────────────────

/**
 * Short descriptions shown in the popover per-domain row when a domain has failed.
 * Intentionally shorter than banner copy — user is already looking at a detail view.
 *
 * Brand guide §34.9.
 */
const errorCategoryLabel: Record<string, string> = {
  AUTH_EXPIRED: 'Credential expired',
  // Slice F bugbash 2026-05-06: distinct label for bad-creds case.
  AUTH_INVALID: 'Credential invalid',
  RATE_LIMIT: 'Rate limited',
  SCHEMA_DRIFT: 'Schema drift — data may be partial',
  TRANSIENT_5XX: 'Server error',
  NETWORK: 'Network error',
  PLATFORM: 'Platform error',
  BUSINESS_EMPTY: 'No data for this scope',
}

// ─── Chip variant classes ─────────────────────────────────────────────────────

/** Returns chip container classes for the given sync state. */
function chipVariantClass(state: LastSyncState): string {
  switch (state) {
    case 'never_synced':
      // chip-muted
      return 'bg-muted border-border text-secondary'
    case 'syncing':
      // chip-syncing — accent/0.08 bg, dark accent text (AA on accent/0.08)
      return 'bg-accent-secondary border-accent text-accent'
    case 'synced_recent':
      // chip-success
      return 'bg-success-foreground border-success text-success'
    case 'synced_stale':
    case 'partial_failure':
    case 'rate_limited':
      // chip-warning
      return 'bg-warning-foreground border-warning text-warning'
    case 'total_failure':
      // chip-danger
      return 'bg-destructive-foreground border-destructive text-destructive'
  }
}

// ─── Popover content components ───────────────────────────────────────────────

interface PopoverDomainRowProps {
  entry: LastSyncPerDomainEntry
}

function PopoverDomainRow({ entry }: PopoverDomainRowProps) {
  const isError = entry.status === 'error'
  const errorLabel = entry.error?.code ? (errorCategoryLabel[entry.error.code] ?? 'Unknown error') : 'Unknown error'

  return (
    <div className="flex items-start gap-2">
      {isError ? (
        /* X icon — danger color */
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="hsl(0 63% 42%)"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
          className="flex-shrink-0 mt-0.5"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ) : (
        /* Check icon — success color */
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="hsl(160.1 84.1% 39.4%)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="flex-shrink-0 mt-0.5"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-xs font-medium',
            isError ? 'text-[hsl(0_63%_42%)]' : 'text-primary',
          )}
        >
          {entry.displayName}
        </p>
        <p
          className={cn(
            'text-[11px]',
            isError ? 'text-[hsl(0_63%_42%_/_0.75)]' : 'text-secondary',
          )}
        >
          {isError
            ? errorLabel
            : entry.lastSyncedAt
              ? formatRelativeTime(entry.lastSyncedAt)
              : 'Not yet synced'}
        </p>
      </div>
    </div>
  )
}

interface PopoverBodyProps {
  state: LastSyncState
  maxLastSyncedAt: string | null
  perDomain: LastSyncPerDomainEntry[]
}

function PopoverBody({ state, maxLastSyncedAt, perDomain }: PopoverBodyProps) {
  const successCount = perDomain.filter(d => d.status === 'success').length
  const totalCount = perDomain.length

  const headerTitle =
    state === 'total_failure'
      ? 'Sync failed'
      : 'Sync details'

  const headerSubtitle =
    state === 'total_failure'
      ? 'All domains failed'
      : state === 'partial_failure'
        ? `${successCount} of ${totalCount} domains synced`
        : maxLastSyncedAt
          ? `Last synced ${formatAbsoluteTimestamp(maxLastSyncedAt)}`
          : 'No sync data yet'

  const isTotalFailure = state === 'total_failure'

  // PR #335 review fix: removed redundant role="dialog" from this inner
  // wrapper. Radix's PopoverContent (desktop) and the BottomSheet outer
  // wrapper (mobile) already provide dialog semantics; nesting another
  // dialog inside causes screen-reader nesting warnings on some AT engines.
  return (
    <div className="min-w-[200px] p-2">
      {/* Header */}
      <div className="px-3.5 pb-2.5 border-b border-border mb-2">
        <p
          className={cn(
            'text-xs font-semibold mb-0.5',
            isTotalFailure ? 'text-destructive' : 'text-primary',
          )}
        >
          {headerTitle}
        </p>
        <p className="text-xs text-secondary">{headerSubtitle}</p>
      </div>

      {/* Domain rows */}
      <div className="px-3.5 flex flex-col gap-2">
        {perDomain.map(entry => (
          <PopoverDomainRow key={entry.domainId} entry={entry} />
        ))}
      </div>

      {/* Stale note (synced_stale only) */}
      {state === 'synced_stale' && maxLastSyncedAt && (
        <div className="px-3.5 mt-2 pt-2 border-t border-border">
          <p className="text-xs text-warning">
            Data is {formatRelativeTime(maxLastSyncedAt)} old — consider re-syncing
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Mobile bottom sheet ──────────────────────────────────────────────────────

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
}

/**
 * Mobile bottom sheet — same content as the Radix Popover but rendered as a
 * full-width slide-up overlay at the bottom of the screen on screens < 640px.
 *
 * Pattern per brand guide §34.7 and §17 (harvest modal mobile treatment).
 * Implements a basic focus trap via onKeyDown.
 */
function BottomSheet({ open, onClose, children }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Focus the sheet when opened
  useEffect(() => {
    if (open && sheetRef.current) {
      sheetRef.current.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        ref={sheetRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Sync status details"
        aria-modal="true"
        className="fixed bottom-0 left-0 right-0 z-50 bg-foreground border-t border-border rounded-t-2xl py-3 shadow-2xl focus-visible:outline-none"
      >
        {/* Drag handle */}
        <div className="w-10 h-1 bg-border rounded-full mx-auto mb-3" aria-hidden="true" />
        <div className="pb-safe-bottom">{children}</div>
      </div>
    </>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

interface LastSyncChipProps {
  /** When true, renders a compact chip with shorter label text for narrow headers. */
  compact?: boolean
}

/**
 * LastSyncChip — always-visible dashboard header sync state indicator.
 *
 * Renders as:
 *   - Non-interactive `<div role="status">` for never_synced, syncing, rate_limited
 *   - Interactive `<button aria-haspopup="dialog">` for all other states (opens popover)
 *
 * Relative time refreshes every 30s via setInterval.
 * Popover uses Radix Popover (desktop) or a bottom sheet (mobile <640px).
 *
 * Brand guide §34.7. TDD §6.4.
 */
export function LastSyncChip({ compact = false }: LastSyncChipProps) {
  const summary = useSelector(selectLastSyncSummary)
  const { state, maxLastSyncedAt, perDomain, retryAfterSec } = summary

  // Tick every 30s to refresh relative-time displays
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000)
    return () => clearInterval(interval)
  }, [])

  // Popover open state (desktop)
  const [popoverOpen, setPopoverOpen] = useState(false)

  // Mobile bottom sheet open state
  const [sheetOpen, setSheetOpen] = useState(false)

  // Detect mobile (<640px) via matchMedia.
  // Guard against jsdom (no matchMedia) — defaults to desktop (false).
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(max-width: 639px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // Countdown state for rate_limited — synced to 1s interval (not the 30s one)
  const [rateLimitCountdown, setRateLimitCountdown] = useState(retryAfterSec ?? 0)
  useEffect(() => {
    if (state !== 'rate_limited' || retryAfterSec == null) {
      setRateLimitCountdown(0)
      return
    }
    setRateLimitCountdown(retryAfterSec)
    const interval = setInterval(() => {
      setRateLimitCountdown(prev => Math.max(0, prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [state, retryAfterSec])

  // Derive the chip label from state + tick (tick triggers re-derive of relative time)
  const label = deriveLabel({ state, maxLastSyncedAt, perDomain, rateLimitCountdown, compact, tick })
  const ariaLabel = deriveAriaLabel({ state, maxLastSyncedAt, perDomain, rateLimitCountdown, tick })

  // Non-interactive states: never_synced, syncing, rate_limited
  const isInteractive = state !== 'never_synced' && state !== 'syncing' && state !== 'rate_limited'

  const chipBaseClass = cn(
    'inline-flex items-center gap-1.5 text-xs font-semibold',
    compact ? 'px-2 py-0.5 rounded-full border' : 'px-2.5 py-1 rounded-full border',
    'transition-opacity duration-150 whitespace-nowrap',
    chipVariantClass(state),
    isInteractive && 'cursor-pointer hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(221.2_83.2%_53.3%)] focus-visible:ring-offset-2',
  )

  const handleClick = () => {
    if (!isInteractive) return
    if (isMobile) {
      setSheetOpen(true)
    } else {
      setPopoverOpen(prev => !prev)
    }
  }

  const popoverContent = (
    <PopoverBody
      state={state}
      maxLastSyncedAt={maxLastSyncedAt}
      perDomain={perDomain}
    />
  )

  const chipIcon = <ChipIcon state={state} compact={compact} />

  if (!isInteractive) {
    // Non-interactive: render as <div role="status">
    const liveProps =
      state === 'syncing' || state === 'rate_limited'
        ? ({ 'aria-live': 'polite' } as const)
        : {}

    return (
      <div
        role="status"
        aria-label={ariaLabel}
        className={chipBaseClass}
        data-testid={`last-sync-chip-${state}`}
        {...liveProps}
      >
        {chipIcon}
        <span>{label}</span>
      </div>
    )
  }

  // Interactive: render as <button> with Radix Popover (desktop) or BottomSheet (mobile)
  const buttonElement = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={isMobile ? sheetOpen : popoverOpen}
      aria-label={ariaLabel}
      className={chipBaseClass}
      onClick={handleClick}
      data-testid={`last-sync-chip-${state}`}
    >
      {chipIcon}
      <span>{label}</span>
    </button>
  )

  if (isMobile) {
    return (
      <>
        {buttonElement}
        <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)}>
          {popoverContent}
        </BottomSheet>
      </>
    )
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>{buttonElement}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="p-0 w-[280px] bg-card border-border rounded-[10px] shadow-[0_4px_20px_rgba(0,0,0,0.10)]"
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  )
}

// ─── Label derivation helpers ─────────────────────────────────────────────────

interface LabelArgs {
  state: LastSyncState
  maxLastSyncedAt: string | null
  perDomain: LastSyncPerDomainEntry[]
  rateLimitCountdown: number
  compact: boolean
  /** Unused directly — presence here ensures useMemo/callers re-run on tick changes. */
  tick: number
}

function deriveLabel({ state, maxLastSyncedAt, perDomain, rateLimitCountdown, compact }: LabelArgs): string {
  switch (state) {
    case 'never_synced':
      return 'Not yet synced'

    case 'syncing':
      return 'Syncing your data...'

    case 'synced_recent': {
      if (!maxLastSyncedAt) return 'Synced'
      const relative = formatRelativeTime(maxLastSyncedAt)
      return compact ? formatCompactTime(maxLastSyncedAt) : `Synced ${relative}`
    }

    case 'synced_stale': {
      if (!maxLastSyncedAt) return 'Synced (stale)'
      const relative = formatRelativeTime(maxLastSyncedAt)
      return compact ? formatCompactTime(maxLastSyncedAt) : `Synced ${relative}`
    }

    case 'partial_failure': {
      const successCount = perDomain.filter(d => d.status === 'success').length
      const totalCount = perDomain.length
      return compact ? 'Partial failure' : `${successCount} of ${totalCount} domains synced — view details`
    }

    case 'total_failure':
      return compact ? 'Failed' : 'Sync failed — view details'

    case 'rate_limited':
      return compact ? `Paused (${rateLimitCountdown}s)` : `Sync paused — retry in ${rateLimitCountdown}s`
  }
}

interface AriaLabelArgs {
  state: LastSyncState
  maxLastSyncedAt: string | null
  perDomain: LastSyncPerDomainEntry[]
  rateLimitCountdown: number
  tick: number
}

function deriveAriaLabel({ state, maxLastSyncedAt, perDomain, rateLimitCountdown }: AriaLabelArgs): string {
  switch (state) {
    case 'never_synced':
      return 'Not yet synced. Build your dashboard to load data.'

    case 'syncing':
      return 'Syncing your data now...'

    case 'synced_recent': {
      const ts = maxLastSyncedAt ? formatAbsoluteTimestamp(maxLastSyncedAt) : ''
      const rel = maxLastSyncedAt ? formatRelativeTime(maxLastSyncedAt) : ''
      return `Synced ${rel} (${ts}). Click for domain breakdown.`
    }

    case 'synced_stale': {
      const rel = maxLastSyncedAt ? formatRelativeTime(maxLastSyncedAt) : ''
      return `Data is stale. Last synced ${rel}. Click for details.`
    }

    case 'partial_failure': {
      const successCount = perDomain.filter(d => d.status === 'success').length
      const totalCount = perDomain.length
      return `Partial sync failure. ${successCount} of ${totalCount} domains synced. Click to view details.`
    }

    case 'total_failure':
      return 'Sync failed for all domains. Click to view error details.'

    case 'rate_limited':
      return `Sync paused. Retry available in ${rateLimitCountdown} seconds.`
  }
}

// ─── Icon per state ───────────────────────────────────────────────────────────

interface ChipIconProps {
  state: LastSyncState
  compact: boolean
}

function ChipIcon({ state, compact }: ChipIconProps) {
  const size = compact ? 10 : 11

  switch (state) {
    case 'never_synced':
      // Muted dot — plain circle, no pulse
      return (
        <span
          className="w-[7px] h-[7px] rounded-full flex-shrink-0 bg-secondary"
          aria-hidden="true"
        />
      )

    case 'syncing':
      // Pulsing blue dot — CSS scale-pulse animation
      return (
        <Loader size={size} className="text-accent animate-spin" aria-hidden="true" />
      )

    case 'synced_recent':
      // Check icon — non-color success signal
      return (
        <CheckIcon
          size={size}
          className="text-success flex-shrink-0"
          strokeWidth={2.5}
          aria-hidden="true"
        />
      )

    case 'synced_stale':
    case 'rate_limited':
      // Clock icon — time-passing signal
      return (
        <ClockIcon
          size={size}
          className="text-warning flex-shrink-0"
          strokeWidth={2}
          aria-hidden="true"
        />
      )

    case 'partial_failure':
      // AlertTriangle — warning, amber
      return (
        <AlertTriangleIcon
          size={size}
          className="text-warning flex-shrink-0"
          strokeWidth={2}
          aria-hidden="true"
        />
      )

    case 'total_failure':
      // X icon — strong failure signal, red
      return (
        <XIcon
          size={size}
          className="text-destructive flex-shrink-0"
          strokeWidth={2.5}
          aria-hidden="true"
        />
      )
  }
}
